import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { SelectFolderGroupsDialogStore } from './select-folder-groups-dialog.store';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { TableColumn, TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { fb, routeParams } from '../../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import { ConfirmBuilder } from '../../../../../shared/service/confirm.builder';
import { FolderGroupObject } from '@libs/types/src/services/object-type/folder/folder-group.object';
import { dateify } from '../../../../../shared/helpers/date.helper';
import { FolderGroupCreateInput } from '@libs/types/src/services/input-type/folder/folder-group.create-input';
import { FolderGroupUpdateInput } from '@libs/types/src/services/input-type/folder/folder-group.update-input';
import { contentGlobalSuccess } from '../../../../content.error';
import { validFromColumn, validToColumn } from '../../../../../shared/components/kit/table/table.fields';
import { ServiceGroupEnum } from '@libs/types/src/services/enum/service-group.enum';
import { FilterInput } from '@it2go/data-grid';
import { AppFormGroup, changeSignal } from '../../../../../../utils/form.utils';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { CancelButtonComponent } from '../../../../../../components/kit/button/cancel-button.component';
import { SaveButtonComponent } from '../../../../../../components/kit/button/save-button.component';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { CardTableComponent } from '../../../../../shared/components/kit/table/card-table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';

@Component({
    selector: 'app-select-folder-groups-dialog',
    templateUrl: 'select-folder-groups-dialog.component.html',
    styleUrl: 'select-folder-groups-dialog.component.scss',
    providers: [SelectFolderGroupsDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        SelectInputComponent,
        DateInputComponent,
        CancelButtonComponent,
        SaveButtonComponent,
        CardTableComponent,
        PrimeTemplate,
        CheckInputComponent,
    ],
})
export class SelectFolderGroupsDialogComponent extends BaseDialogComponent {

    public readonly store = inject(SelectFolderGroupsDialogStore);
    public readonly confirm = inject(ConfirmBuilder);

    protected prefix = 'folder.groups.';

    protected columns: TableColumn[] = [
        { name: 'group' },
        validFromColumn,
        validToColumn,
    ];

    protected form = new AppFormGroup({
        id: fb.control<number | null>(null),
        groupId: fb.control<number | null>(null, Validators.required),
        validFrom: fb.control<Date | null>(null),
        validTo: fb.control<Date | null>(null),
        primary: fb.control<boolean>(false, { nonNullable: true }),
    });

    protected additionalFilters = computed<FilterInput[][]>(() => {
        return FilterBuilder.filter({
            folderId: routeParams.folderId(),
            type: this.type,
        }).filter || [];
    });

    protected headerTitle = computed(() => {
        const groupId = this.formGroupChanged();
        if (groupId) {
            return 'folder.groups.editGroup';
        } else {
            return 'folder.groups.newGroup';
        }
    });

    protected readonly ServiceGroupEnum = ServiceGroupEnum;
    protected type: ServiceGroupEnum;

    private folderTable = viewChild<CardTableComponent>(CardTableComponent);
    private formGroupChanged = changeSignal<number | null>(this.form.controls.groupId);

    constructor() {
        super();

        this.type = this.dialogConfig.data.type;
        if (this.type === ServiceGroupEnum.SOCIAL_TARGET) {
            this.columns.splice(1, 0, {
                name: 'primary',
                type: TableColumnType.Bool,
                filterable: false,
                sortable: false,
            });
        }

        const folderId = routeParams.folderId();
        this.store.api.getServiceGroups(FilterBuilder.singlePage({
            folderId,
            type: this.type,
        })).then();
    }

    protected updateGroup(group: FolderGroupObject): void {
        this.form.patchValue({
            id: group.id,
            groupId: group.groupId,
            validFrom: dateify(group.validFrom),
            validTo: dateify(group.validTo),
            primary: group.primary,
        });
    }

    protected async submit(): Promise<void> {
        if (this.form.isInvalid()) return;
        const _data = {
            validFrom: this.form.value.validFrom?.toISOString() || null,
            validTo: this.form.value.validTo?.toISOString() || null,
            groupId: this.form.value.groupId,
            primary: this.type === ServiceGroupEnum.SOCIAL_TARGET
                ? this.form.value.primary
                : false,
        };

        if (this.form.value.id) {
            const data = _data as FolderGroupUpdateInput;
            data.id = this.form.value.id;
            this.store.api.updateFolderGroup(data).then(() => {
                this.form.reset();
                this.folderTable()?.reload();
                contentGlobalSuccess.next('common.updated');
            });
        } else {
            const data = _data as FolderGroupCreateInput;
            data.folderId = Number(routeParams.folderId());
            data.creditGroup = this.type === ServiceGroupEnum.CREDITS;
            this.store.api.createFolderGroup(data).then(() => {
                this.form.reset();
                this.folderTable()?.reload();
                contentGlobalSuccess.next('common.assigned');
            });
        }
    }

    protected deleteGroup(event: any): void {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteFolderGroup({ id: event.item.id }).then(() => {
                    this.folderTable()?.reload();
                    contentGlobalSuccess.next('common.deleted');
                });
            },
            event,
        );
    }

}
